<template>
    <div class="tab-container" :aria-busy="loading">
        <p class="sr-only" role="status">{{ feedStatus }}</p>
        <SharedSpinner v-if="loading" label="Loading activity history..." />
        <div v-else-if="activities.length === 0 && activityPurged" class="empty-state-container">
            <h3 class="empty-state-title">{{ PURGED_ACTIVITY_TITLE }}</h3>
            <div class="empty-state-message">
                <p>{{ PURGED_ACTIVITY_MESSAGE }}</p>
            </div>
        </div>
        <div v-else-if="activities.length === 0" class="empty-state-container">
            <h3 class="empty-state-title">No Activity Found</h3>
            <div class="empty-state-message">
                <p>We're actively checking {{ selectedCompany?.name }} for exposures.</p>
                <p>Check back soon!</p>
            </div>
        </div>
        <div v-else>
            <div class="activity" v-for="(activity, index) in activities" :key="activity.message_id">
                <div class="header">
                    <div class="timeline-icon">
                        <div class="icon-wrapper">
                            <PhXCircle v-if="isFailed(activity)" :size="24" class="text-red-600" weight="fill" />
                            <PhCheckCircle v-else-if="isPositiveAction(activity)" :size="24" class="text-green-600" weight="fill" />
                            <PhWarningCircle v-else-if="isExposureDetected(activity)" :size="24" class="text-red-600" weight="fill" />
                            <PhCheckCircle v-else :size="24" class="text-green-600" weight="fill" />
                        </div>
                        <div v-if="index !== activities.length - 1" class="timeline-line"></div>
                    </div>
                    <div class="activity-content">
                        <div class="timeline-header">
                            <div class="header-status">
                                <span class="timeline-title">{{ getMessageText(activity.message.content.message) }}</span>
                                <div class="logo-status">
                                    <PrimeBadge v-if="isRemovalInProgress(activity)" severity="blue" class="!whitespace-nowrap">
                                        <PhSpinnerGap :size="12" class="spinner" />
                                        <span>Removal In Progress</span>
                                    </PrimeBadge>
                                    <PrimeBadge v-else-if="isFailedForBadge(activity)" severity="error" class="!whitespace-nowrap">
                                        <PhX :size="12" weight="fill" />
                                        <span>Failed</span>
                                    </PrimeBadge>
                                    <PrimeBadge v-else-if="isClosed(activity)" class="!whitespace-nowrap">
                                        <PhCheck :size="12" />
                                        <span>Closed</span>
                                    </PrimeBadge>
                                </div>
                            </div>
                            <span class="timeline-date">{{ formatActivityDate(activity.timestamp_ms) }}</span>
                        </div>

                        <div v-if="hasDetails(activity)" class="timeline-card">
                            <div class="details">
                                <div class="tags">
                                    <PrimeTag v-if="activity.scan?.first_name || activity.scan?.last_name">
                                        {{ [activity.scan.first_name, activity.scan.last_name].filter(Boolean).join(' ') }}
                                    </PrimeTag>
                                    <PrimeTag v-if="activity.scan?.age">
                                        Age: {{ activity.scan.age }}
                                    </PrimeTag>
                                    <PrimeTag v-if="activity.scan?.date_of_birth">
                                        DOB: {{ formatDOB(activity.scan.date_of_birth) }}
                                    </PrimeTag>
                                    <PrimeTag v-for="(email, idx) in activity.scan?.emails || []" :key="`email-${idx}`">
                                        {{ email }}
                                    </PrimeTag>
                                    <PrimeTag v-for="(phone, idx) in activity.scan?.phones || []" :key="`phone-${idx}`">
                                        {{ phone }}
                                    </PrimeTag>
                                    <PrimeTag v-for="(address, idx) in activity.scan?.addresses || []" :key="`address-${idx}`">
                                        {{ formatAddress(address) }}
                                    </PrimeTag>
                                    <PrimeTag v-for="(aka, idx) in activity.scan?.aka || []" :key="`aka-${idx}`">
                                        {{ aka }}
                                    </PrimeTag>
                                    <PrimeTag v-for="(relative, idx) in activity.scan?.relatives || []" :key="`relative-${idx}`">
                                        Relative: {{ formatRelative(relative) }}
                                    </PrimeTag>
                                </div>
                                <a
                                    v-if="profileUrl(activity)"
                                    :href="profileUrl(activity)!"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="profile-link"
                                >
                                    Public Exposure Link
                                    <span class="sr-only">on {{ profileHost(activity) }} (opens in new tab)</span>
                                    <PhArrowSquareOut :size="12" aria-hidden="true" />
                                </a>
                            </div>
                            
                            <div v-if="hasScreenshots(activity)" class="screenshots">
                                <button
                                    type="button"
                                    class="screenshot-title"
                                    :aria-expanded="expandedScreenshots.has(activity.message_id)"
                                    @click="toggleScreenshot(activity.message_id)"
                                >
                                    <span>Screenshots</span>
                                    <span class="separator"></span>
                                    <PhCaretDown v-if="!expandedScreenshots.has(activity.message_id)" :size="16" class="text-zinc-400" weight="fill" aria-hidden="true" />
                                    <PhCaretUp v-else :size="16" class="text-zinc-400" weight="fill" aria-hidden="true" />
                                </button>
                                <div v-if="expandedScreenshots.has(activity.message_id)" class="images-wrapper">
                                    <button
                                        v-for="(screenshot, idx) in getScreenshots(activity)"
                                        :key="`screenshot-${idx}`"
                                        type="button"
                                        :aria-label="`View screenshot ${idx + 1}`"
                                        @click="viewImage(screenshot)"
                                    >
                                        <img :src="screenshot" alt="" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { PhArrowSquareOut, PhCaretDown, PhCaretUp, PhCheck, PhCheckCircle, PhSpinnerGap, PhWarningCircle, PhX, PhXCircle } from '@phosphor-icons/vue';
import { useDynamicDialog } from '~/composables/dynamic-dialog';
import { formatDate } from '@privacy-bee/format/date';
import { useCompaniesStore } from '~/stores/person/companies';
import type { ConversationMessage } from '~/types/companies/conversations';
import { ModalNames } from '~/types/modals';
import { isActivityPurged, PURGED_ACTIVITY_MESSAGE, PURGED_ACTIVITY_TITLE } from '~/utils/activity-retention';

const companyStore = useCompaniesStore();
const { selectedCompany } = storeToRefs(companyStore);

const store = usePersonStore();
const expandedScreenshots = ref(new Set<string>());

const props = defineProps<{
    activities: ConversationMessage[];
    loading: boolean;
    exposureStatus?: string | null;
    /** When the exposure was last seen; past the 90-day TTL an empty feed means expired history. */
    lastSeenAt?: string | null;
}>();

const activityPurged = computed((): boolean => isActivityPurged(props.lastSeenAt));

// The parent mounts this component with `loading` already true, so a busy-only region would enter
// the tree holding its text and never announce. The resolved count is the reliable trigger.
const feedStatus = computed((): string => {
    if (props.loading) return 'Loading activity history';
    if (!props.activities.length) return activityPurged.value ? PURGED_ACTIVITY_TITLE : 'No activity found';
    return `${props.activities.length} activity ${props.activities.length === 1 ? 'entry' : 'entries'}`;
});

const formatActivityDate = (timestamp_ms: number): string => {
    try {
        const timestampStr = String(timestamp_ms);
        const truncatedTimestamp = timestampStr.slice(0, -7);
        const numericTimestamp = Number(truncatedTimestamp);
        if (isNaN(numericTimestamp) || numericTimestamp <= 0) {
            return 'Invalid Date';
        }
        const date = new Date(numericTimestamp * 1000);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return formatDate(date);
    } catch (error) {
        console.error('Error formatting activity date:', error);
        return 'Invalid Date';
    }
};

const viewImage = (url: string) => {
    store.imgViewerUrl = url;
    useDynamicDialog(ModalNames.IMAGE_VIEWER, {
        imageUrl: url,
        altText: 'Exposure Image',
    });
};

const toggleScreenshot = (messageId: string) => {
    if (expandedScreenshots.value.has(messageId)) {
        expandedScreenshots.value.delete(messageId);
    } else {
        expandedScreenshots.value.add(messageId);
    }
};

const isFailed = (activity: ConversationMessage): boolean => {
    const messageText = getMessageText(activity.message.content.message).toLowerCase();
    const content = activity.message.content as { message: string; status?: string };
    
    // Privacy request has failed
    if (messageText.includes('request has failed') || 
        messageText.includes('privacy request has failed') ||
        messageText.includes('has failed')) {
        return true;
    }
    
    // Exposure has been closed but status is failed - show red warning
    if (messageText.includes('exposure has been closed') && 
        (props.exposureStatus === 'failed' || props.exposureStatus === 'FAILED')) {
        return true;
    }
    
    // Status-based check for failed
    if (content.status === 'failed') {
        return true;
    }
    
    return false;
};

const isExposureDetected = (activity: ConversationMessage): boolean => {
    const messageText = getMessageText(activity.message.content.message).toLowerCase();
    return messageText.includes('found exposed') || 
           messageText.includes('detected') || 
           messageText.includes('exposure');
};

const isPositiveAction = (activity: ConversationMessage): boolean => {
    const messageText = getMessageText(activity.message.content.message).toLowerCase();
    const content = activity.message.content as { message: string; status?: string };
    
    // Not found exposed - this is good news!
    if (messageText.includes('not found exposed') || 
        messageText.includes('was not found exposed')) {
        return true;
    }
    
    // Company confirmed exposure request
    if (messageText.includes('company has confirmed') || 
        messageText.includes('confirmed your exposure request') ||
        messageText.includes('confirmed your request')) {
        return true;
    }
    
    // Privacy request has been submitted
    if (messageText.includes('privacy request has been submitted') || 
        messageText.includes('privacy request has been assigned') ||
        messageText.includes('request has been submitted') ||
        messageText.includes('deletion request has been submitted')) {
        return true;
    }
    
    // Exposure has been closed - check actual exposure status
    if (messageText.includes('exposure has been closed')) {
        // If exposure status is 'failed' or 'FAILED', this should show red warning, not green check
        // So return false here and let isFailed handle it
        if (props.exposureStatus === 'failed' || props.exposureStatus === 'FAILED') {
            return false;
        }
        // If status is 'closed' or 'CLOSED', show green check
        return true;
    }
    
    // Any request submitted (but not exposure detected)
    if (messageText.includes('submitted') && 
        (messageText.includes('request') || messageText.includes('deletion') || messageText.includes('privacy'))) {
        return true;
    }
    
    // Status-based checks for closed/removed
    if (content.status === 'closed' || content.status === 'removed') {
        return true;
    }
    
    return false;
};

const isRemovalInProgress = (activity: ConversationMessage): boolean => {
    const messageText = getMessageText(activity.message.content.message).toLowerCase();
    const content = activity.message.content as { message: string; status?: string };
    // Only show in-progress badge, but don't use red icon for submitted requests
    return (content.status === 'in_progress' || 
            content.status === 'pending') &&
           !messageText.includes('submitted');
};

const isFailedForBadge = (activity: ConversationMessage): boolean => {
    // Check if exposure has been closed but status is failed
    const messageText = getMessageText(activity.message.content.message).toLowerCase();
    if (messageText.includes('exposure has been closed') && 
        (props.exposureStatus === 'failed' || props.exposureStatus === 'FAILED')) {
        return true;
    }
    return false;
};

const isClosed = (activity: ConversationMessage): boolean => {
    const messageText = getMessageText(activity.message.content.message).toLowerCase();
    const content = activity.message.content as { message: string; status?: string };
    
    // Don't show "Closed" badge if it's actually failed
    if (messageText.includes('exposure has been closed') && 
        (props.exposureStatus === 'failed' || props.exposureStatus === 'FAILED')) {
        return false;
    }
    
    return (content.status === 'closed' || 
            content.status === 'removed') &&
           (messageText.includes('submitted') || messageText.includes('request'));
};

const hasDetails = (activity: ConversationMessage): boolean => {
    return !!(activity.scan?.first_name || 
              activity.scan?.last_name ||
              activity.scan?.age ||
              activity.scan?.date_of_birth ||
              activity.scan?.emails?.length || 
              activity.scan?.phones?.length ||
              activity.scan?.addresses?.length ||
              activity.scan?.aka?.length ||
              activity.scan?.relatives?.length ||
              activity.scan?.screenshot ||
              profileUrl(activity) ||
              getScreenshotUrl(activity.message.content.message));
};

const formatAddress = (address: { line1?: string; line2?: string; city?: string; state?: string; postal_code?: string; country?: string }): string => {
    const parts: string[] = [];
    if (address.line1) parts.push(address.line1);
    if (address.line2) parts.push(address.line2);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.postal_code) parts.push(address.postal_code);
    if (address.country) parts.push(address.country);
    return parts.filter(Boolean).join(', ');
};

const formatRelative = (relative: { first_name?: string; last_name?: string; name?: string; age?: string }): string => {
    if (relative.name) return relative.name;
    const nameParts = [relative.first_name, relative.last_name].filter(Boolean);
    if (nameParts.length > 0) {
        return nameParts.join(' ') + (relative.age ? ` (Age: ${relative.age})` : '');
    }
    return '';
};

const formatDOB = (dob: string): string => {
    if (!dob) return '';
    
    try {
        // Handle ISO format with time (e.g., "1993-09-07T00:00:00Z")
        const dateStr = dob.split('T')[0];
        const [year, month, day] = dateStr.split('-');
        
        if (year && month && day) {
            // Format as MM/DD/YYYY
            return `${month}/${day}/${year}`;
        }
        
        // If already in MM/DD/YYYY format, return as is
        if (dob.includes('/')) {
            return dob;
        }
        
        return dob;
    } catch (error) {
        // If parsing fails, return original
        return dob;
    }
};

const hasScreenshots = (activity: ConversationMessage): boolean => {
    return !!(activity.scan?.screenshot || getScreenshotUrl(activity.message.content.message));
};

const getScreenshots = (activity: ConversationMessage): string[] => {
    const screenshots: string[] = [];
    const messageScreenshot = getScreenshotUrl(activity.message.content.message);
    if (messageScreenshot) {
        screenshots.push(messageScreenshot);
    }
    if (activity.scan?.screenshot) {
        screenshots.push(activity.scan.screenshot);
    }
    return screenshots;
};

const getScreenshotUrl = (content: string): string | null => {
    if (!content) return null;
    const screenshotMatch = content.match(/Screenshot:\s*(.+?)(?:\n|$)/i);
    if (screenshotMatch && screenshotMatch[1]) {
        let url = screenshotMatch[1].trim();
        url = url.replace(/[)\]}\s]+$/, '');
        if (url.includes('[object') || url.includes('Object]')) {
            return null;
        }
        const urlOnlyMatch = url.match(/(https?:\/\/\S+)/i);
        if (urlOnlyMatch) {
            url = urlOnlyMatch[1];
        }
        return toHttpUrl(url);
    }
    return null;
};

const toHttpUrl = (url: string): string | null => {
    try {
        const urlObj = new URL(url);
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            return null;
        }
        if (!urlObj.hostname || urlObj.hostname.length === 0) {
            return null;
        }
        return url;
    } catch (e) {
        return null;
    }
};

const profileUrl = (activity: ConversationMessage): string | null => {
    const raw = activity.scan?.url?.trim();
    if (!raw) return null;
    return toHttpUrl(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
};

const profileHost = (activity: ConversationMessage): string => {
    const url = profileUrl(activity);
    return url ? new URL(url).hostname : '';
};

const getMessageText = (content: string): string => {
    if (!content) return '';
    const textContent = content.replace(/Screenshot:\s*https?:\/\/[^\s]+/i, '').trim();
    return textContent;
};

// Automatically expand screenshots for activities that have them
watch(() => props.activities, (newActivities) => {
    if (newActivities && newActivities.length > 0) {
        newActivities.forEach((activity) => {
            if (hasScreenshots(activity)) {
                expandedScreenshots.value.add(activity.message_id);
            }
        });
    }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.tab-container {
    @apply p-1 lg:p-4;
}

.activity {
    @apply py-0;

    .header {
        @apply flex flex-row gap-3 w-full;

        .timeline-icon {
            @apply flex flex-col items-center self-stretch gap-1 pb-1;

            .icon-wrapper {
                @apply flex items-center justify-center;
            }

            .timeline-line {
                @apply w-px flex-1 rounded-sm bg-zinc-200;
            }
        }

        .activity-content {
            @apply flex flex-col items-start gap-3 flex-1 pb-8;
        }

        .timeline-header {
            @apply flex flex-col items-start w-full;

            .header-status {
                @apply flex flex-col gap-1 lg:gap-0 lg:flex-row items-start lg:items-center justify-between w-full;

                .timeline-title {
                    @apply text-zinc-950 text-sm font-medium leading-5;
                }

                .logo-status {
                    @apply flex justify-end items-center gap-2;
                }
            }

            .timeline-date {
                @apply text-zinc-600 text-sm font-normal leading-5 whitespace-nowrap;
            }
        }

        .timeline-card {
            @apply flex flex-col items-start self-stretch rounded border border-zinc-300 bg-white p-3 shadow-cardshadow;

            .details {
                @apply flex flex-col items-start gap-1 w-full;

                .tags {
                    @apply flex flex-wrap items-start content-start gap-2 flex-1 w-full;
                }

                .profile-link {
                    @apply flex gap-1 items-center pt-1 text-blue-600 hover:underline text-xs font-semibold leading-none;
                }
            }

            .screenshots {
                @apply flex flex-col items-start gap-2 pt-4 w-full;

                .screenshot-title {
                    @apply flex items-center gap-2 w-full cursor-pointer bg-transparent border-0 p-0 text-left;
                    font-family: inherit;

                    span {
                        @apply text-zinc-950 text-xs font-medium leading-3 whitespace-nowrap;
                    }

                    .separator {
                        @apply h-px flex-1 bg-zinc-300;
                    }
                }

                .images-wrapper {
                    @apply flex items-start justify-between w-full gap-3 flex-wrap;

                    button {
                        @apply flex bg-transparent border-0 p-0 cursor-pointer;
                    }

                    img {
                        @apply w-[125px] h-[130px] object-cover rounded cursor-pointer;
                    }
                }
            }
        }
    }
}

.spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.empty-state-container {
    @apply flex flex-col items-center justify-center py-12 gap-1 text-center;
    
    .empty-state-title {
        @apply text-base font-semibold text-gray-900 leading-6 not-italic;
    }
    
    .empty-state-message {
        @apply flex flex-col text-gray-600 text-sm not-italic leading-5;
    }
}
</style>
